import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { LanguageContext } from '../LanguageContext';
import api from '../services/api';
import ModalDialog from './ModalDialog';
import styles from './AdminPanel.module.css';

const AdminNotificationsPanel = () => {
  const { t } = useContext(LanguageContext);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedSettings, setSelectedSettings] = useState(null);
  const [deviceSettings, setDeviceSettings] = useState([]);
  const [history, setHistory] = useState([]);
  const [confirmAction, setConfirmAction] = useState(null);
  const [messageModal, setMessageModal] = useState(null);

  const loadUsers = useCallback(async () => {
    const [usersRes, historyRes] = await Promise.all([
      api.get('/admin/notification-users/'),
      api.get('/admin/notifications/history/'),
    ]);
    setUsers(usersRes.data || []);
    setHistory(historyRes.data || []);
    if (!selectedUserId && usersRes.data?.length) {
      setSelectedUserId(String(usersRes.data[0].user_id));
    }
  }, [selectedUserId]);

  const loadSelected = useCallback(async () => {
    if (!selectedUserId) {
      setSelectedSettings(null);
      setDeviceSettings([]);
      return;
    }
    const [detailRes, deviceRes] = await Promise.all([
      api.get(`/admin/notification-users/${selectedUserId}/`),
      api.get(`/admin/notification-users/${selectedUserId}/device-settings/`),
    ]);
    setSelectedSettings(detailRes.data);
    setDeviceSettings(deviceRes.data || []);
  }, [selectedUserId]);

  useEffect(() => {
    loadUsers().catch((error) => {
      console.error('Admin notifications load error:', error);
    });
  }, [loadUsers]);

  useEffect(() => {
    loadSelected().catch((error) => {
      console.error('Admin notification user load error:', error);
    });
  }, [loadSelected]);

  const selectedUser = useMemo(
    () => users.find((item) => String(item.user_id) === String(selectedUserId)),
    [users, selectedUserId]
  );

  const saveUserSettings = async (e) => {
    e.preventDefault();
    if (!selectedSettings) {
      return;
    }
    setConfirmAction({
      title: t('notificationSettingsAdmin'),
      message: t('adminConfirmNotificationEdit'),
      onConfirm: async () => {
        try {
          const response = await api.patch(`/admin/notification-users/${selectedUserId}/`, {
            ...selectedSettings.notification_settings,
            admin_confirmed: true,
          });
          setSelectedSettings(response.data);
          await loadUsers();
          await loadSelected();
        } catch (error) {
          console.error('Admin save notification settings error:', error);
          setMessageModal({ title: t('notificationSettingsAdmin'), message: t('saveError') });
        } finally {
          setConfirmAction(null);
        }
      },
    });
  };

  const updateDeviceToggle = async (deviceId, field, value) => {
    setConfirmAction({
      title: t('notificationSettingsAdmin'),
      message: t('adminConfirmNotificationEdit'),
      onConfirm: async () => {
        try {
          const response = await api.patch(`/admin/notification-users/${selectedUserId}/device-settings/${deviceId}/`, {
            [field]: value,
            admin_confirmed: true,
          });
          setDeviceSettings((prev) => prev.map((row) => (row.device === deviceId ? response.data : row)));
        } catch (error) {
          console.error('Admin update device settings error:', error);
          setMessageModal({ title: t('notificationSettingsAdmin'), message: t('saveError') });
        } finally {
          setConfirmAction(null);
        }
      },
    });
  };

  return (
    <>
      {confirmAction && (
        <ModalDialog
          title={confirmAction.title}
          message={confirmAction.message}
          confirmLabel={t('confirmAction')}
          cancelLabel={t('cancel')}
          onConfirm={confirmAction.onConfirm}
          onClose={() => setConfirmAction(null)}
        />
      )}
      {messageModal && (
        <ModalDialog
          title={messageModal.title}
          message={messageModal.message}
          confirmLabel={t('save')}
          cancelLabel={t('close')}
          hideConfirm
          onConfirm={() => setMessageModal(null)}
          onClose={() => setMessageModal(null)}
        />
      )}
      <section className={styles.card}>
        <div className={styles.sectionHeader}>
          <h2>{t('notificationSettingsAdmin')}</h2>
          <select value={selectedUserId} onChange={(e) => setSelectedUserId(e.target.value)}>
            <option value="">{t('selectUser')}</option>
            {users.map((user) => (
              <option key={user.user_id} value={user.user_id}>
                {user.username}
              </option>
            ))}
          </select>
        </div>

        {selectedSettings && (
          <form onSubmit={saveUserSettings} className={styles.inlineForm}>
            <label className={styles.inlineField}>
              <span>{t('contactEmail')}</span>
              <input
                type="email"
                value={selectedSettings.notification_settings.contact_email || ''}
                onChange={(e) =>
                  setSelectedSettings({
                    ...selectedSettings,
                    notification_settings: {
                      ...selectedSettings.notification_settings,
                      contact_email: e.target.value,
                    },
                  })
                }
                placeholder={t('contactEmail')}
              />
            </label>
            <label className={styles.inlineField}>
              <span>{t('preferredLanguage')}</span>
              <select
                value={selectedSettings.notification_settings.preferred_language || 'pl'}
                onChange={(e) =>
                  setSelectedSettings({
                    ...selectedSettings,
                    notification_settings: {
                      ...selectedSettings.notification_settings,
                      preferred_language: e.target.value,
                    },
                  })
                }
              >
                <option value="pl">Polski</option>
                <option value="en">English</option>
                <option value="de">Deutsch</option>
              </select>
            </label>
            <label className={styles.inlineField}>
              <span>{t('offlineThresholdMinutes')}</span>
              <input
                type="number"
                min="1"
                value={selectedSettings.notification_settings.offline_threshold_minutes ?? 360}
                onChange={(e) =>
                  setSelectedSettings({
                    ...selectedSettings,
                    notification_settings: {
                      ...selectedSettings.notification_settings,
                      offline_threshold_minutes: Number(e.target.value || 0),
                    },
                  })
                }
                placeholder={t('offlineThresholdMinutes')}
              />
            </label>
            <label className={styles.inlineField}>
              <span>{t('warningThresholdPercent')}</span>
              <input
                type="number"
                step="0.1"
                min="0"
                value={selectedSettings.notification_settings.warning_threshold_percent ?? 70}
                onChange={(e) =>
                  setSelectedSettings({
                    ...selectedSettings,
                    notification_settings: {
                      ...selectedSettings.notification_settings,
                      warning_threshold_percent: Number(e.target.value || 0),
                    },
                  })
                }
                placeholder={t('warningThresholdPercent')}
              />
            </label>
            <button className={styles.primaryButton} type="submit">{t('save')}</button>
          </form>
        )}

        {selectedUser && (
          <p>
            {t('assignedFilters')}: {selectedUser.assigned_device_count}
          </p>
        )}

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('devices')}</th>
                <th>MAC</th>
                <th>{t('notifyOffline')}</th>
                <th>{t('notifyReconnected')}</th>
                <th>{t('notifyFilterWarning')}</th>
                <th>{t('notifyFilterReplacement')}</th>
                <th>{t('notifyFilterReplacementReminder')}</th>
              </tr>
            </thead>
            <tbody>
              {deviceSettings.map((row) => (
                <tr key={row.device}>
                  <td>{row.device_name}</td>
                  <td>{row.mac}</td>
                  <td><input type="checkbox" checked={!!row.notify_offline} onChange={(e) => updateDeviceToggle(row.device, 'notify_offline', e.target.checked)} /></td>
                  <td><input type="checkbox" checked={!!row.notify_reconnected} onChange={(e) => updateDeviceToggle(row.device, 'notify_reconnected', e.target.checked)} /></td>
                  <td><input type="checkbox" checked={!!row.notify_filter_warning} onChange={(e) => updateDeviceToggle(row.device, 'notify_filter_warning', e.target.checked)} /></td>
                  <td><input type="checkbox" checked={!!row.notify_filter_replacement} onChange={(e) => updateDeviceToggle(row.device, 'notify_filter_replacement', e.target.checked)} /></td>
                  <td><input type="checkbox" checked={!!row.notify_filter_replacement_reminder} onChange={(e) => updateDeviceToggle(row.device, 'notify_filter_replacement_reminder', e.target.checked)} /></td>
                </tr>
              ))}
              {deviceSettings.length === 0 && (
                <tr>
                  <td colSpan={7} className={styles.emptyRow}>{t('noResults')}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className={styles.card}>
        <h2>{t('notificationHistoryAdmin')}</h2>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('last')}</th>
                <th>{t('username')}</th>
                <th>{t('devices')}</th>
                <th>{t('notificationType')}</th>
                <th>{t('status')}</th>
                <th>{t('recipient')}</th>
                <th>{t('notificationDetails')}</th>
              </tr>
            </thead>
            <tbody>
              {history.map((row) => (
                <tr key={row.id}>
                  <td>{new Date(row.created_at).toLocaleString()}</td>
                  <td>{row.username || t('adminRole')}</td>
                  <td>{row.device_name || row.mac || '-'}</td>
                  <td>{t(`notificationType_${row.notification_type}`)}</td>
                  <td>{t(`notificationStatus_${row.status}`)}</td>
                  <td>{row.recipient_email || '-'}</td>
                  <td>{row.error_message || '-'}</td>
                </tr>
              ))}
              {history.length === 0 && (
                <tr>
                  <td colSpan={7} className={styles.emptyRow}>{t('noResults')}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
};

export default AdminNotificationsPanel;
